import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../useApi';
import type { Tenant } from '../api';
import { ErrorNotice } from '../components/ErrorNotice';
import { MARKETING_CONSENT_LABEL, MARKETING_CONSENT_SCOPE } from '../marketingConsentCopy.js';

// Onboarding is deliberately two visible steps.
//
// Step 1 collects what the customer's admin reads off their own Acumatica
// screen. Step 2 hands control to that instance's own consent page. We never
// ask for an Acumatica username or password — the whole selling point of a
// Connected solution is that the only footprint in their ERP is an OAuth app
// they registered themselves.

// Deliberately NOT derived from window.location.origin. This Static Web App
// answers on both app.acutidy.com and its *.azurestaticapps.net default
// hostname, so the browser's origin is not reliably the one the API sends as
// redirect_uri — and a mismatch makes Acumatica reject the authorization with
// an error the customer cannot act on. The server owns this value; we display
// whatever it reports.

export function TenantOnboardingPage() {
  const api = useApi();
  const navigate = useNavigate();

  const [companyName, setCompanyName] = useState('');
  const [instanceBaseUrl, setInstanceBaseUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [redirectUri, setRedirectUri] = useState<string | null>(null);

  // Billing shape. $199 buys a production/sandbox PAIR, so onboarding has to
  // ask two things the old form never did: which half of a pair this instance
  // is, and whether it joins an account (and pair) that already exists. Get
  // this wrong and the customer is either double-charged or unable to add
  // their sandbox at all.
  const [instanceKind, setInstanceKind] = useState<'production' | 'sandbox'>('production');
  // Starts false and stays false unless ticked. Never pre-ticked: consent that
  // has to be opted out of is not consent.
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [accountChoice, setAccountChoice] = useState('new');
  const [pairChoice, setPairChoice] = useState('new');
  const [existing, setExisting] = useState<Tenant[]>([]);

  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    api
      .get<{ oauthRedirectUri: string }>('/config')
      .then((c) => {
        if (!cancelled) setRedirectUri(c.oauthRedirectUri);
      })
      .catch(() => {
        // Leave it null rather than guessing. Showing a plausible-but-wrong URI
        // is worse than showing none: the admin would register it, and the
        // failure would surface much later as an opaque Acumatica error.
        if (!cancelled) setRedirectUri(null);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  useEffect(() => {
    if (!api) return;
    api
      .get<{ tenants: Tenant[] }>('/tenants')
      .then(({ tenants }) => setExisting(tenants))
      // Not fatal: without it the form simply offers a new account, which is
      // the right default for a first-time customer anyway.
      .catch(() => setExisting([]));
  }, [api]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!api || submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      const { tenant } = await api.post<{ tenant: Tenant }>('/tenants', {
        companyName,
        instanceBaseUrl,
        // Always null. The field it fed was proven inert on September 14,
        // 2026 (see acumatica/client.ts) — Acumatica binds the tenant to the
        // client id, so there was nothing for a customer to tell us here.
        acumaticaCompany: null,
        clientId,
        clientSecret,
        instanceKind,
        marketingConsent,
        // Omitted entirely when starting fresh — the API mints both ids, and
        // sending nulls would read as "join nothing", which is not a thing.
        ...(accountChoice !== 'new' ? { accountId: accountChoice } : {}),
        ...(accountChoice !== 'new' && pairChoice !== 'new' ? { pairId: pairChoice } : {}),
      });

      // Straight into the consent redirect. Leaving the admin on a "saved!"
      // screen invites them to walk away from a tenant that cannot do anything
      // yet, and they will come back assuming it is set up.
      const { authorizeUrl } = await api.get<{ authorizeUrl: string }>(
        `/tenants/${tenant.tenantId}/oauth/authorize`
      );
      window.location.assign(authorizeUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setSubmitting(false);
    }
  }

  // Accounts the caller already has, labelled by whichever instance best
  // identifies them — the production one if there is one.
  const accounts = [...new Map(existing.map((t) => [t.accountId, t])).keys()].map(
    (accountId) => {
      const members = existing.filter((t) => t.accountId === accountId);
      const label =
        members.find((t) => t.instanceKind === 'production')?.companyName ??
        members[0]?.companyName ??
        'Existing account';
      return { accountId, label, members };
    }
  );

  const selectedAccount = accounts.find((a) => a.accountId === accountChoice);

  // Only pairs that do not already hold an instance of the kind being added.
  // The database enforces one production and one sandbox per pair with a
  // unique index; filtering here means the customer never picks a combination
  // that comes back as a constraint violation they cannot interpret.
  const joinablePairs = selectedAccount
    ? [...new Set(selectedAccount.members.map((t) => t.pairId))]
        .map((pairId) => {
          const members = selectedAccount.members.filter((t) => t.pairId === pairId);
          return {
            pairId,
            label: members.map((m) => `${m.companyName} (${m.instanceKind})`).join(' + '),
            takenKinds: new Set(members.map((m) => m.instanceKind)),
          };
        })
        .filter((p) => !p.takenKinds.has(instanceKind))
    : [];

  return (
    <>
      <div className="page-header">
        <h1>Connect an Acumatica instance</h1>
      </div>

      <div className="card instructions">
        <h2>First, register AcuTidy in Acumatica</h2>
        <p className="muted">
          In the instance you want to connect, open{' '}
          <strong>Connected Applications (SM303010)</strong> and add an
          application:
        </p>
        <ol>
          <li>
            Set <strong>OAuth 2.0 Flow</strong> to{' '}
            <strong>Authorization Code</strong>.
          </li>
          <li>
            Add this exact <strong>Redirect URI</strong>:
            <br />
            {redirectUri ? (
              <code className="mono">{redirectUri}</code>
            ) : (
              <span className="muted">Loading…</span>
            )}
          </li>
          <li>
            Save, then copy the generated <strong>Client ID</strong> and{' '}
            <strong>Client Secret</strong> into the form below. The secret is
            shown once — copy it before leaving the screen.
          </li>
        </ol>
        <p className="muted small">
          The client secret is stored in Azure Key Vault, never in the
          application database, and is never sent back to this browser.
        </p>
      </div>

      <ErrorNotice message={error} />

      <form className="card" onSubmit={(e) => void handleSubmit(e)}>
        <fieldset className="choice-group">
          <legend>What is this instance?</legend>
          <label className="choice">
            <input
              type="radio"
              name="instanceKind"
              value="production"
              checked={instanceKind === 'production'}
              onChange={() => {
                setInstanceKind('production');
                setPairChoice('new');
              }}
            />
            <span>
              <strong>Production</strong>
              <small className="muted">Your live company data.</small>
            </span>
          </label>
          <label className="choice">
            <input
              type="radio"
              name="instanceKind"
              value="sandbox"
              checked={instanceKind === 'sandbox'}
              onChange={() => {
                setInstanceKind('sandbox');
                setPairChoice('new');
              }}
            />
            <span>
              <strong>Sandbox</strong>
              <small className="muted">
                A test or training copy. Free when paired with a production
                instance.
              </small>
            </span>
          </label>
        </fieldset>

        {accounts.length > 0 && (
          <label>
            <span>Billing account</span>
            <select
              value={accountChoice}
              onChange={(e) => {
                setAccountChoice(e.target.value);
                setPairChoice('new');
              }}
            >
              <option value="new">Create a new account (billed separately)</option>
              {accounts.map((a) => (
                <option key={a.accountId} value={a.accountId}>
                  {a.label} — {a.members.length} instance
                  {a.members.length === 1 ? '' : 's'}
                </option>
              ))}
            </select>
            <small className="muted">
              Instances in one account share a subscription, a card and an
              invoice.
            </small>
          </label>
        )}

        {selectedAccount && (
          <label>
            <span>Pair with</span>
            <select value={pairChoice} onChange={(e) => setPairChoice(e.target.value)}>
              <option value="new">Start a new pair (adds $199/month)</option>
              {joinablePairs.map((p) => (
                <option key={p.pairId} value={p.pairId}>
                  {p.label} — no extra charge
                </option>
              ))}
            </select>
            <small className="muted">
              {joinablePairs.length === 0
                ? `Every pair in this account already has a ${instanceKind} instance, so this one starts a new pair.`
                : `A pair is one production instance plus one sandbox, billed once at $199/month. Adding this ${instanceKind} to an existing pair costs nothing.`}
            </small>
          </label>
        )}

        <label>
          <span>Company name</span>
          <input
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Acme Manufacturing"
            required
            maxLength={200}
          />
          <small className="muted">How this instance is labeled in AcuTidy.</small>
        </label>

        <label>
          <span>Instance base URL</span>
          <input
            value={instanceBaseUrl}
            onChange={(e) => setInstanceBaseUrl(e.target.value)}
            placeholder="https://acme.acumatica.com"
            type="url"
            required
            maxLength={500}
          />
          <small className="muted">
            The instance root, with no trailing slash and no <code>/entity</code>{' '}
            path.
          </small>
        </label>

        <label>
          <span>Client ID</span>
          <input
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="mono"
            required
            maxLength={200}
          />
        </label>

        <label>
          <span>Client secret</span>
          <input
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            type="password"
            className="mono"
            required
            maxLength={500}
            autoComplete="off"
          />
        </label>

        {/* Deliberately separate from anything required, and worded so that
            leaving it unticked has no effect on the service. Bundling this
            with sign-up would make the consent worthless. */}
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={marketingConsent}
            onChange={(e) => setMarketingConsent(e.target.checked)}
          />
          <span>
            {MARKETING_CONSENT_LABEL}
            <br />
            <small className="muted">Optional. {MARKETING_CONSENT_SCOPE}</small>
          </span>
        </label>

        <div className="form-actions">
          <button type="button" className="link-button" onClick={() => navigate('/tenants')}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={submitting || !api}>
            {submitting ? 'Saving…' : 'Save and authorize in Acumatica'}
          </button>
        </div>
      </form>
    </>
  );
}
